import React, { useEffect, useRef, useState } from "react";
import { Lock, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * AdminLoginModal — Shadcn dialog opened by the 10-tap easter-egg on the
 * Worthify logo. Validates the pasted ADMIN_TOKEN by issuing a single
 * read-only `GET /api/admin/stale-sessions` (any 200/4xx body proves the
 * header round-tripped to the backend; 401 = wrong token, 200 = correct).
 *
 * On success we stash the token in `sessionStorage` (key:
 * `worthify_admin_token`) and navigate to the admin console; the console
 * page pre-fills its input from the same key.
 */
const TOKEN_STORAGE_KEY = "worthify_admin_token";
const ADMIN_CONSOLE_URL = "/api/admin/console";

export const AdminLoginModal = ({ open, onOpenChange }) => {
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Reset state every time the dialog opens; focus the input.
  useEffect(() => {
    if (!open) return;
    setToken("");
    setError("");
    setSubmitting(false);
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmed = token.trim();
    if (!trimmed) {
      setError("Enter the admin token to continue.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${backend}/api/admin/stale-sessions`, {
        method: "GET",
        headers: { "X-Admin-Token": trimmed },
      });
      if (res.status === 401) {
        setError("Invalid token. Try again.");
        setSubmitting(false);
        return;
      }
      if (!res.ok && res.status !== 200) {
        setError(`Server returned ${res.status}. Check backend logs.`);
        setSubmitting(false);
        return;
      }
      try {
        window.sessionStorage.setItem(TOKEN_STORAGE_KEY, trimmed);
      } catch {
        /* private mode — console page will just require manual paste */
      }
      // Same-tab navigation with the token in the query string. The
      // backend uses the `?t=` param to gate the HTML render (added in
      // H40d after the public URL leak); the admin page itself
      // immediately strips the param from window.history so the secret
      // never sits in the address bar / browser history.
      const sep = ADMIN_CONSOLE_URL.includes("?") ? "&" : "?";
      window.location.href = `${ADMIN_CONSOLE_URL}${sep}t=${encodeURIComponent(trimmed)}`;
    } catch (err) {
      setError(`Network error: ${err?.message || "unknown"}`);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-slate-800 dark:text-slate-100"
        data-testid="admin-login-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <DialogTitle className="text-slate-800 dark:text-slate-100">Admin access</DialogTitle>
          </div>
          <DialogDescription className="text-slate-400">
            Paste your operator token to open the Worthify admin console.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              ref={inputRef}
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                if (error) setError("");
              }}
              placeholder="ADMIN_TOKEN"
              data-testid="admin-token-input"
              className="pl-9 bg-slate-950 border-slate-700 text-slate-800 dark:text-slate-100 placeholder:text-slate-600 font-mono text-sm focus-visible:ring-emerald-500"
              disabled={submitting}
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-2 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2"
              data-testid="admin-login-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="admin-login-cancel"
              className="text-slate-300 hover:text-slate-800 dark:text-slate-100 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !token.trim()}
              data-testid="admin-login-submit"
              className="bg-emerald-600 hover:bg-emerald-500 text-slate-900 dark:text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying…
                </>
              ) : (
                "Open console"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminLoginModal;
